import { Component, isDevMode, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { config } from '@app/core/app-config';
import { SignupFlowService } from '@app/modules/signup-new/services/signup-flow.service';
import { SignupUserService } from '@app/modules/signup-new/services/signup-user.service';
import { SignupWizardService } from '@app/modules/signup-new/services/signup-wizard.service';
import { QueryParams, SegmentEvent } from '@app/shared/interfaces';
import { FingerprintService } from '@app/shared/services/fingerprint/fingerprint.service';
import { SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { RecaptchaComponent } from 'ng-recaptcha';
import { EMPTY } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';

const INVITE_GUIDE_URL =
  'http://help.hospitable.com/en/articles/4631570-i-received-an-invite-to-access-smartbnb-should-i-sign-up-and-create-a-new-account';

@Component({ selector: 'sbnb-signup-email-page', templateUrl: './signup-email-page.component.html' })
export class SignupEmailPageComponent implements OnInit {
  config = config;
  validateLoading = false;
  createLoading = false;
  airbnbErrorMessage?: string;
  action: 'continue' | 'guide' | 'existing' = 'continue';
  form = new UntypedFormGroup({
    email: new UntypedFormControl('', [Validators.required, Validators.email]),
    agreeToTerms: new UntypedFormControl(false, [Validators.requiredTrue]),
    code: new UntypedFormControl(),
  });

  @ViewChild('captchaRef') captchaRef: RecaptchaComponent;

  constructor(
    public signupUserService: SignupUserService,
    private signupWizardService: SignupWizardService,
    private route: ActivatedRoute,
    private segmentIoService: SegmentIoService,
    private signupFlowService: SignupFlowService,
    private router: Router,
    private fingerprintService: FingerprintService
  ) {}

  ngOnInit() {
    this.segmentIoService.track(SegmentEvent.SignUpWithAirbnbShown, {});

    this.route.queryParamMap.subscribe((queryParam: ParamMap) => {
      if (queryParam.has('code')) {
        this.form.controls.code.setValue(queryParam.get('code'));
      }

      if (queryParam.has('email')) {
        this.form.controls.email.setValue(queryParam.get('email'));
      }
    });

    this.route.queryParams.subscribe((params: Params) => {
      if (params[QueryParams.AirbnbError]) {
        this.airbnbErrorMessage =
          params[QueryParams.AirbnbErrorMessage] ||
          'Sorry something went wrong during the Airbnb signup, please try again or continue with email.';
      }
    });
  }

  onContinue() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.validateLoading = true;
    this.signupUserService
      .validateEmail(this.form.controls.email.value)
      .pipe(
        catchError((err) => {
          this.setEmailError(err.error?.errors?.email[0]);
          return EMPTY;
        }),
        finalize(() => (this.validateLoading = false))
      )
      .subscribe((user) => {
        if (user.status === 'new') {
          if (isDevMode()) {
            this.createNewUser();
          } else {
            this.captchaRef.execute();
          }

          return;
        }

        if (user.status === 'returning') {
          this.signupUserService.user$.next(user);
          this.router.navigate(['/signup/returning']);
        } else if (user.status === 'invited') {
          this.action = 'guide';
          this.form.controls.email.setErrors({ custom: 'Please accept the invite you have received.' });
        } else if (user.status === 'existing') {
          this.signupUserService.user$.next(user);
          this.action = 'existing';
          this.form.controls.email.setErrors({ custom: 'An account with this email already exists.' });
        } else if (user.status === 'login_restricted') {
          this.signupUserService.user$.next(user);
          this.router.navigate(['/signup/restricted']);
        }
      });
  }

  createNewUser() {
    this.createLoading = true;

    this.fingerprintService
      .getVisitorData()
      .pipe(
        switchMap((data) => {
          return this.signupUserService.createUser({
            email: this.form.controls.email.value,
            agreed_to_tos: this.form.controls.agreeToTerms.value,
            flow: this.signupFlowService.flow$.value,
            code: this.form.controls.code.value,
            referral_id: window.growsurf?.getReferrerId(),
            sealed_result: data.sealedResult,
          });
        }),
        catchError((err) => {
          this.setEmailError(err.error?.errors?.email[0]);
          return EMPTY;
        }),
        finalize(() => {
          this.createLoading = false;
          this.captchaRef.reset();
        })
      )
      .subscribe((user) => {
        this.signupUserService.user$.next(user);
        this.signupWizardService.init();
      });
  }

  private setEmailError(custom?: string) {
    this.form.controls.email.setErrors({ custom: custom || 'We were unable to complete your signup at this time' });
  }

  revertAction() {
    this.action = 'continue';
  }

  guideInvited() {
    window.location.href = INVITE_GUIDE_URL;
  }
}
